import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../api/endpoints';
import { CURRENCIES, EMAIL_PREFERENCE_TYPES } from '../utils/constants';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';

import './Settings.css';
import { 
  FaSave, FaUser, FaEnvelope, FaBell, FaShieldAlt, FaGlobe, FaCheckCircle, FaPaperPlane, FaLock 
} from 'react-icons/fa';

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [profileForm, setProfileForm] = useState({
    display_name: '',
    bank_account: '',
    local_currency: 'NGN',
  });
  const [emailPrefs, setEmailPrefs] = useState({});
  const isEmailVerified = user?.email_verified === true;

  useEffect(() => {
    if (user) {
      setProfileForm({
        display_name: user.display_name || '',
        bank_account: user.bank_account || '',
        local_currency: user.local_currency || 'NGN',
      });
    }
    fetchEmailPreferences();
  }, [user]);

  const fetchEmailPreferences = async () => {
    try {
      const response = await userAPI.getEmailPreferences();
      setEmailPrefs(response.data || {});
    } catch (error) {
      console.error('Failed to fetch email preferences:', error);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await userAPI.updateProfile(profileForm);
      updateUser(profileForm);
      toast.success('Profile settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailPrefToggle = async (key) => {
    const newPrefs = { ...emailPrefs, [key]: !emailPrefs[key] };
    setEmailPrefs(newPrefs);
    try {
      await userAPI.updateEmailPreferences(newPrefs);
      toast.success('Email preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
      // Revert on error
      setEmailPrefs(emailPrefs);
    }
  };

// ==================== PASSWORD CHANGE ====================
const handlePasswordSubmit = async (e) => {
  e.preventDefault();
  setChangingPassword(true);
  try {
    const res = await userAPI.changePassword(passwordForm);
    if (res.data?.success) {
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } else {
      toast.error(res.data?.message || 'Failed to change password');
    }
  } catch (err) {
    toast.error(err.response?.data?.message || 'Failed to change password');
  } finally {
    setChangingPassword(false);
  }
};

// ==================== EMAIL VERIFICATION ====================
const handleSendVerification = async () => {
  setSendingVerify(true);
  try {
    const res = await userAPI.sendVerificationEmail();
    if (res.data?.success) {
      if (res.data.already_verified) {
        toast.success('Your email is already verified');
      } else {
        toast.success('Verification email sent — check your inbox');
        setVerifyCooldown(60);
      }
    } else {
      toast.error(res.data?.message || 'Failed to send verification email');
    }
  } catch (err) {
    toast.error(err.response?.data?.message || 'Failed to send verification email');
  } finally {
    setSendingVerify(false);
  }
};

const handleResendVerification = () => {
  if (verifyCooldown > 0) return;
  handleSendVerification();
};

  // Password change form
const [passwordForm, setPasswordForm] = useState({
  current_password: '',
  new_password: '',
  confirm_password: '',
});
const [changingPassword, setChangingPassword] = useState(false);
const [showPasswords, setShowPasswords] = useState(false);

// Email verification
const [sendingVerify, setSendingVerify] = useState(false);
const [verifyCooldown, setVerifyCooldown] = useState(0);

// Cooldown countdown effect
useEffect(() => {
  if (verifyCooldown <= 0) return;
  const t = setInterval(() => setVerifyCooldown((n) => Math.max(0, n - 1)), 1000);
  return () => clearInterval(t);
}, [verifyCooldown]);


  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p className="settings-subtitle">Manage your account preferences</p>
      </div>

      <div className="settings-layout">
        {/* Sidebar */}
        <div className="settings-sidebar">
          <button 
            className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <FaUser /> Profile
          </button>
          <button 
            className={`settings-tab ${activeTab === 'email' ? 'active' : ''}`}
            onClick={() => setActiveTab('email')}
          >
            <FaEnvelope /> Email Preferences
          </button>
          <button 
            className={`settings-tab ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <FaBell /> Notifications
          </button>
           <button 
            className={`settings-tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
            >
            <FaShieldAlt /> Security
            </button>
          <button 
            className={`settings-tab ${activeTab === 'currency' ? 'active' : ''}`}
            onClick={() => setActiveTab('currency')}
          >
            <FaGlobe /> Currency
          </button>
        </div>

        {/* Content */}
        <div className="settings-content">
          {/* Profile */}
          {activeTab === 'profile' && (
            <div className="settings-card">
              <h2>Profile Settings</h2>
              <form onSubmit={handleProfileSubmit}>
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={profileForm.display_name}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, display_name: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Bank Account Details</label>
                  <input
                    type="text"
                    placeholder="Enter your bank details"
                    value={profileForm.bank_account}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, bank_account: e.target.value }))}
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? <LoadingSpinner size="small" /> : <><FaSave /> Save Changes</>}
                </button>
              </form>
            </div>
          )}

          {/* Email Preferences */}
          {activeTab === 'email' && (
            <div className="settings-card">
              <h2>Email Preferences</h2>
              <p className="settings-description">
                Choose which emails you want to receive
              </p>
              <div className="email-prefs-list">
                {EMAIL_PREFERENCE_TYPES.map((pref) => (
                  <div key={pref.key} className="email-pref-item">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={emailPrefs[pref.key] !== false}
                        onChange={() => handleEmailPrefToggle(pref.key)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <div className="pref-info">
                      <span className="pref-icon">{pref.icon}</span>
                      <div>
                        <strong>{pref.label}</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Currency */}
          {activeTab === 'currency' && (
            <div className="settings-card">
              <h2>Currency Settings</h2>
              <div className="form-group">
                <label>Local Currency</label>
                <select
                  value={profileForm.local_currency}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, local_currency: e.target.value }))}
                >
                  {Object.entries(CURRENCIES).map(([code, data]) => (
                    <option key={code} value={code}>
                      {data.symbol} {code} - {data.name}
                    </option>
                  ))}
                </select>
              </div>
              <button onClick={handleProfileSubmit} className="btn btn-primary">
                <FaSave /> Save Currency
              </button>
            </div>
          )}

          {/* Security */}
         {activeTab === 'security' && (
            <div className="settings-card">
              <h2>Security</h2>

              {/* ---------- Email Verification status ---------- */}
              <div className="security-block">
                <div className="security-block-header">
                  <div>
                    <strong>Email Verification</strong>
                    <p>
                      {isEmailVerified
                        ? `Your email (${user?.email}) is verified.`
                        : `Verify your email (${user?.email}) to unlock all features.`}
                    </p>
                  </div>
                  {isEmailVerified ? (
                    <span className="verified-pill">
                      <FaCheckCircle /> Verified
                    </span>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={handleResendVerification}
                      disabled={sendingVerify || verifyCooldown > 0}
                      type="button"
                    >
                      {sendingVerify ? (
                        <LoadingSpinner size="small" />
                      ) : verifyCooldown > 0 ? (
                        <>Resend in {verifyCooldown}s</>
                      ) : (
                        <><FaPaperPlane /> Send Verification Email</>
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="security-divider" />

              {/* ---------- Change Password ---------- */}
              <div className="security-block">
                <div className="security-block-header">
                  <div>
                    <strong>Password</strong>
                    <p>Change your account password</p>
                  </div>
                </div>

                <form onSubmit={handlePasswordSubmit} className="password-form">
                  <div className="form-group">
                    <label>Current Password</label>
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm((p) => ({ ...p, current_password: e.target.value }))}
                      autoComplete="current-password"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm((p) => ({ ...p, new_password: e.target.value }))}
                      autoComplete="new-password"
                      minLength={8}
                      required
                    />
                    <small className="field-hint">
                      At least 8 characters, with letters and numbers.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm((p) => ({ ...p, confirm_password: e.target.value }))}
                      autoComplete="new-password"
                      minLength={8}
                      required
                    />
                  </div>

                  <label className="show-passwords-toggle">
                    <input
                      type="checkbox"
                      checked={showPasswords}
                      onChange={(e) => setShowPasswords(e.target.checked)}
                    />
                    <span>Show passwords</span>
                  </label>

                  <button type="submit" className="btn btn-primary" disabled={changingPassword}>
                    {changingPassword ? (
                      <LoadingSpinner size="small" />
                    ) : (
                      <><FaLock /> Change Password</>
                    )}
                  </button>
                </form>
              </div>

              <div className="security-divider" />

              {/* ---------- 2FA placeholder ---------- */}
              <div className="security-block">
                <div className="security-block-header">
                  <div>
                    <strong>Two-Factor Authentication</strong>
                    <p>Add an extra layer of security to your account</p>
                  </div>
                  <button className="btn btn-secondary" type="button" disabled>
                    Coming soon
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <div className="settings-card">
              <h2>Notification Settings</h2>
              <p className="settings-description">
                Manage how you receive notifications
              </p>
              <div className="notification-settings">
                <div className="notif-setting">
                  <label className="toggle-label">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                  <span>Push Notifications</span>
                </div>
                <div className="notif-setting">
                  <label className="toggle-label">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                  <span>Email Notifications</span>
                </div>
                <div className="notif-setting">
                  <label className="toggle-label">
                    <input type="checkbox" />
                    <span className="toggle-slider"></span>
                  </label>
                  <span>SMS Notifications</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;